// triggerOps — pure structure operations for conditional logic (IF rules) on
// checkList fields. All functions take and return plain arrays, no React —
// FormBuilderPage funnels them through its dirty-marking updaters. The flat
// list invariants they maintain: an ask_a_question follow-up row sits inside
// its parent's contiguous block (directly after the parent, after earlier
// rules' follow-up blocks), carries the parent's `sectionId`, its own
// `triggerFlowReferenceId` (the uuid the parent's wire entry names) and
// `triggerParentId` (the parent's client id). Deletions cascade recursively —
// removing a rule removes its follow-up row AND everything that row's own
// rules spawned (legacy removeSingleTriggerType semantics), which the
// block-range walk gives for free.

import { v4 as uuidv4 } from 'uuid';
import type { BuilderField, BuilderPage, BuilderRule } from './builderModel';
import {
  createElementProperties,
  newFieldUniqueId,
  type ChoiceOption,
  type LibraryElementType,
} from './formElementDefaults';
import { fieldBlockRange } from './canvasItems';

/** A fresh, incomplete rule — no answer picked, no action. Lives in state
 *  (edits are live) and is silently dropped at save until completed. */
export const newRule = (): BuilderRule => ({ id: uuidv4(), matchingValue: '', action: null });

/** What the drawer's THEN tiles/sub-panels request — the page's setRuleAction
 *  turns an ask spec into a real follow-up element (insertRuleChild). */
export type RuleActionSpec =
  | { kind: 'ask_a_question'; childType: LibraryElementType }
  | { kind: 'require_picture' }
  | { kind: 'require_video' }
  | { kind: 'require_note' }
  | { kind: 'user_page'; pageId: string };

const optionListOf = (field: BuilderField): ChoiceOption[] => {
  const basic = (field.properties as unknown as Record<string, Record<string, unknown>>)
    .basicProperties;
  const list = basic?.optionList;
  return Array.isArray(list) ? (list as ChoiceOption[]) : [];
};

export const hasRules = (field: BuilderField): boolean => field.rules.length > 0;

/** The IF button's enablement rule (legacy checkIfMultiChoiceDisabled,
 *  inverted): a checkList with at least one option and single choice. */
export const isLogicEligible = (field: BuilderField): boolean =>
  field.elementType === 'checkList' &&
  optionListOf(field).length > 0 &&
  (field.properties.basicProperties as Record<string, unknown>).multipleChoice !== true;

/** Answer values already used by the field's rules — an option may be picked
 *  by at most ONE rule, so these are excluded from the other rules' pickers.
 *  `excludeRuleId` keeps a rule's own value out of its own way. */
export const takenRuleValues = (field: BuilderField, excludeRuleId?: string): Set<string> => {
  const taken = new Set<string>();
  for (const rule of field.rules) {
    if (rule.id !== excludeRuleId && rule.matchingValue !== '') taken.add(rule.matchingValue);
  }
  return taken;
};

/** Whether any answer value is still free for a NEW rule — gates "Add rule". */
export const hasFreeRuleValue = (field: BuilderField): boolean => {
  const taken = takenRuleValues(field);
  return optionListOf(field).some((o) => !taken.has(o.value));
};

/** Rule-nesting depth of a field: 0 = ordinary, 1 = follow-up, 2 = follow-up
 *  of a follow-up (which may not host rules of its own — max 2 levels). */
export const triggerDepth = (fields: BuilderField[], fieldId: string): number => {
  let depth = 0;
  let current = fields.find((f) => f.id === fieldId);
  while (current?.triggerParentId) {
    depth += 1;
    current = fields.find((f) => f.id === current?.triggerParentId);
    if (depth > 8) break; // cycle guard — malformed state, not a real depth
  }
  return depth;
};

/** Where a NEW follow-up of `ruleId` goes: after the follow-up blocks of every
 *  EARLIER rule of the same parent (so row order mirrors rule order), else
 *  directly after the parent row (legacy findSurpassIndex, block-based). */
const childInsertionIndex = (
  fields: BuilderField[],
  parent: BuilderField,
  ruleId: string,
): number => {
  const parentIndex = fields.findIndex((f) => f.id === parent.id);
  let at = parentIndex + 1;
  for (const rule of parent.rules) {
    if (rule.id === ruleId) break;
    if (rule.action?.kind !== 'ask_a_question') continue;
    const range = fieldBlockRange(fields, rule.action.childId);
    if (range && range[1] > at) at = range[1];
  }
  return at;
};

/** Create an ask_a_question follow-up for `ruleId` and place it (surpass
 *  placement). Does NOT touch the rule's action — the caller pairs this with
 *  its own rule update so both land in one state write. */
export const insertRuleChild = (
  fields: BuilderField[],
  parentId: string,
  ruleId: string,
  childType: LibraryElementType,
): { fields: BuilderField[]; childId: string } => {
  const parent = fields.find((f) => f.id === parentId);
  if (!parent) return { fields, childId: '' };
  const id = uuidv4();
  const child: BuilderField = {
    id,
    _id: null,
    elementGlobalId: null,
    uniqueIdToReplace: id,
    // A follow-up lives wherever its parent lives — same section or top-level.
    sectionId: parent.sectionId,
    elementType: childType,
    // Born untitled — the properties panel is where it gets its question text.
    properties: createElementProperties(childType),
    collapsed: false,
    rules: [],
    triggerFlowReferenceId: uuidv4(),
    triggerParentId: parentId,
  };
  const at = childInsertionIndex(fields, parent, ruleId);
  return { fields: [...fields.slice(0, at), child, ...fields.slice(at)], childId: id };
};

/** Remove a field row AND its whole trigger-descendant block. Rules pointing
 *  at removed rows die with their owners (they're inside the block). */
export const deleteFieldBlock = (fields: BuilderField[], fieldId: string): BuilderField[] => {
  const range = fieldBlockRange(fields, fieldId);
  if (!range) return fields;
  return [...fields.slice(0, range[0]), ...fields.slice(range[1])];
};

/** Delete one rule, cascading: an ask_a_question rule takes its follow-up row
 *  and everything that row's own rules spawned (recursively, via the block). */
export const deleteRuleCascade = (
  fields: BuilderField[],
  parentId: string,
  ruleId: string,
): BuilderField[] => {
  const parent = fields.find((f) => f.id === parentId);
  const rule = parent?.rules.find((r) => r.id === ruleId);
  if (!parent || !rule) return fields;
  let next = fields;
  if (rule.action?.kind === 'ask_a_question') {
    next = deleteFieldBlock(next, rule.action.childId);
  }
  return next.map((f) =>
    f.id === parentId ? { ...f, rules: f.rules.filter((r) => r.id !== ruleId) } : f,
  );
};

/** Option values present in `prev` but gone from `next` — an option deletion
 *  and a value edit both surface as a missing old value (legacy semantics).
 *  Additions and reorders yield nothing. */
export const removedOptionValues = (prev: ChoiceOption[], next: ChoiceOption[]): string[] => {
  const kept = new Set(next.map((o) => o.value));
  return [...new Set(prev.map((o) => o.value).filter((v) => !kept.has(v)))];
};

/** Kill the rules of `fieldId` whose matchingValue is among `removedValues`
 *  ('all' = every rule — the response-set-applied case), cascading each like
 *  deleteRuleCascade. One pass, one new array. */
export const cascadeOptionChange = (
  fields: BuilderField[],
  fieldId: string,
  removedValues: string[] | 'all',
): BuilderField[] => {
  const field = fields.find((f) => f.id === fieldId);
  if (!field || !field.rules.length) return fields;
  const gone = removedValues === 'all' ? null : new Set(removedValues);
  const dying = field.rules.filter((r) => (gone ? gone.has(r.matchingValue) : true));
  if (!dying.length) return fields;

  // Collect every row id inside the dying rules' follow-up blocks, then drop
  // them all in one filter — index-free, so cascades can't trip over shifts.
  const doomedRows = new Set<string>();
  for (const rule of dying) {
    if (rule.action?.kind !== 'ask_a_question') continue;
    const range = fieldBlockRange(fields, rule.action.childId);
    if (!range) continue;
    for (let i = range[0]; i < range[1]; i += 1) doomedRows.add(fields[i].id);
  }
  const dyingIds = new Set(dying.map((r) => r.id));
  return fields
    .filter((f) => !doomedRows.has(f.id))
    .map((f) => (f.id === fieldId ? { ...f, rules: f.rules.filter((r) => !dyingIds.has(r.id)) } : f));
};

/** Deep-copy a run of rows with fresh identities, preserving their internal
 *  structure: new client ids/uniqueIdToReplace, nulled server ids, restamped
 *  fieldUniqueIds, fresh triggerFlowReferenceIds where one existed, and
 *  sectionId/triggerParentId/rule-childId references re-pointed at the copies.
 *  References leaving the run: sectionId keeps its value (the caller decides
 *  whether the copy stays in the same section), a triggerParentId outside the
 *  run is dropped (a follow-up must not be copied without its parent), an
 *  ask rule whose child is outside the run is dropped, and user_page targets
 *  are deliberately KEPT (a duplicated rule still goes to the ORIGINAL page —
 *  legacy updateUniqueIdValues semantics). */
export const copyRowsRemapped = (rows: BuilderField[]): BuilderField[] => {
  const idMap = new Map<string, string>(rows.map((f) => [f.id, uuidv4()]));
  return rows.map((f) => {
    const id = idMap.get(f.id) as string;
    const properties = structuredClone(f.properties);
    if ('otherProperties' in properties) {
      properties.otherProperties.fieldUniqueId = newFieldUniqueId();
    }
    return {
      ...f,
      id,
      _id: null,
      elementGlobalId: null,
      uniqueIdToReplace: id,
      properties,
      sectionId: f.sectionId ? (idMap.get(f.sectionId) ?? f.sectionId) : null,
      triggerParentId: f.triggerParentId ? (idMap.get(f.triggerParentId) ?? null) : null,
      triggerFlowReferenceId: f.triggerFlowReferenceId ? uuidv4() : null,
      rules: f.rules.flatMap((rule): BuilderRule[] => {
        if (rule.action?.kind === 'ask_a_question') {
          const childId = idMap.get(rule.action.childId);
          if (!childId) return [];
          return [{ ...rule, id: uuidv4(), action: { kind: 'ask_a_question', childId } }];
        }
        return [{ ...rule, id: uuidv4() }];
      }),
    };
  });
};

/** Duplicate a field's whole block (row + trigger descendants), inserting the
 *  copies directly after the original block. */
export const duplicateFieldBlock = (fields: BuilderField[], fieldId: string): BuilderField[] => {
  const range = fieldBlockRange(fields, fieldId);
  if (!range) return fields;
  const copies = copyRowsRemapped(fields.slice(range[0], range[1]));
  return [...fields.slice(0, range[1]), ...copies, ...fields.slice(range[1])];
};

/** Drop every user_page rule that targets `deletedPageId`, across all pages
 *  (legacy deleteTriggerTypesOfDeletedPage). No cascade needed — a go-to rule
 *  has no follow-up rows. */
export const stripGotoRulesToPage = (pages: BuilderPage[], deletedPageId: string): BuilderPage[] =>
  pages.map((page) => ({
    ...page,
    fields: page.fields.map((f) =>
      f.rules.some((r) => r.action?.kind === 'user_page' && r.action.pageId === deletedPageId)
        ? {
            ...f,
            rules: f.rules.filter(
              (r) => !(r.action?.kind === 'user_page' && r.action.pageId === deletedPageId),
            ),
          }
        : f,
    ),
  }));

/** Go-to graph for the page picker's guards: source page id → the page ids its
 *  fields' rules jump to. Derived on demand (legacy kept gotoPageTriggerMapObj
 *  in state and had to maintain it through every mutation — deriving can't
 *  drift). A page is unpickable from page A when A already targets it, or when
 *  it targets A back (two-way block). */
export const deriveGotoMap = (pages: BuilderPage[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  for (const page of pages) {
    const targets = new Set<string>();
    for (const field of page.fields) {
      for (const rule of field.rules) {
        if (rule.action?.kind === 'user_page') targets.add(rule.action.pageId);
      }
    }
    if (targets.size) map.set(page.id, targets);
  }
  return map;
};
